<template>
  <div class="detail">
    <!-- 详情信息 -->
    <div class="main">
      <div class="info">
        <span class="name">{{ customSpuRef?.name || currentCustomSpuData.customSpu?.name || '--' }}</span>
        <!-- 商品名字修改框 -->
        <a-popover v-model:visible="nameEditor.state.flag"
                   trigger="click"
                   placement="right"
                   @visible-change="handleClickChange">
          <template #content>
            <a-input v-model:value="nameRef"
                     :placeholder="$t('CustomSpus.CustomSpuDetail.productName')"
                     class="my-input" />
            <a-button type="primary"
                      style="margin-right: 12px"
                      @click="onSaveName">{{ $t('CustomSpus.CustomSpuDetail.save') }}</a-button>
            <a-button @click="nameEditor.changeFlag()">{{ $t('CustomSpus.CustomSpuDetail.cancel') }}</a-button>
          </template>
          <edit-outlined class="my-icon" />
        </a-popover>
      </div>
      <AddToCart ref="addToCart"
                 :hide-info="true" />
    </div>
    <!-- 按钮 -->
    <div class="operation">
      <a-button class="btn"
                @click="toDesign">{{ $t('CustomSpus.CustomSpuDetail.redesign') }}</a-button>
      <a-button class="btn"
                :type="currentBtn === 'detailsShow' ? 'primary' : ''"
                @click="detailsShow">{{ $t('CustomSpus.CustomSpuDetail.viewDetails') }}</a-button>
      <a-button class="btn"
                :type="currentBtn === 'addProduct' ? 'primary' : ''"
                @click="addProduct">{{ $t('CustomSpus.CustomSpuDetail.addToCart') }}</a-button>
      <a-button class="delete"
                @click="deleteChange">{{ $t('CustomSpus.CustomSpuDetail.delete') }}</a-button>
    </div>
  </div>

  <!-- 详情框 -->
  <a-modal v-model:visible="details.state.flag"
           :closable="false"
           footer=""
           :body-style="{ padding: '10px', height: '800px', overflow: 'auto' }">
    <a-image v-for="item in detailsValue"
             :key="item"
             width="100%"
             :src="toUrl(item.image)" />
    <a-button type="primary"
              class="fb-spu-images-download"
              block
              @click="downloadImages">
      <vertical-align-bottom-outlined />
      {{ $t('CustomSpus.CustomSpuDetail.downloadDetails') }}
    </a-button>
  </a-modal>
</template>
<script setup lang="ts">
import { toUrl } from '@/utils'
import { ExclamationCircleOutlined } from '@ant-design/icons-vue'
import { message, Modal } from 'ant-design-vue'
import { saveAs } from 'file-saver'
import JSZip from 'jszip'
import { createVNode, reactive, ref, watch, nextTick, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useHead } from '@vueuse/head'
import { deleteCustomSpu, fetchSpuImages, updateCustomSpuName } from '../../api'
import { selectedSpuData } from "../../index"
import type {
  Color,
  CustomSpu, SimpleSku, SpuImage
} from '../../types'
import AddToCart from './AddToCart.vue'
import { useI18n } from 'vue-i18n'
import { createProductSchema, toJsonLd } from '&/utils/schema'

const currentCustomSpuData = selectedSpuData()
const { t } = useI18n()
const modal = () => {
  const state = reactive({
    flag: false,
  })

  const changeFlag = () => {
    state.flag = !state.flag
  }

  return {
    state,
    changeFlag,
  }
}

const currentBtn = ref<string>('')

const props = defineProps<{
  colors: Color[]
  sizes: string[]
  customSkus: SimpleSku[]
}>()

const customSpuRef = ref<CustomSpu>()

// 计算 Product Schema
const productSchema = computed(() => {
  const spu = customSpuRef.value?.spu
  const customSpu = customSpuRef.value
  if (!spu || !customSpu) return null

  // 获取价格
  let price = 0
  if (customSpu.price_detail?.custom_price?.length > 0) {
    price = customSpu.price_detail.custom_price[0].price || 0
  }

  // 获取图片
  let image = ''
  if (customSpu.customizedImages?.length > 0 && customSpu.customizedImages[0].customableImage) {
    image = toUrl(customSpu.customizedImages[0].customableImage.image) || ''
  } else if (spu.images?.length > 0) {
    image = toUrl(spu.images[0]) || ''
  }

  return createProductSchema({
    name: customSpu.name || spu.name,
    description: spu.description || '高品质定制产品',
    url: window.location.href,
    image: image,
    sku: customSpu.id?.toString(),
    offers: {
      price: price,
      priceCurrency: 'CNY',
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
    },
  })
})

// 使用 useHead 注入 Product Schema
useHead({
  script: computed(() => {
    if (!productSchema.value) return []
    return [{
      type: 'application/ld+json',
      innerHTML: toJsonLd(productSchema.value),
    }]
  }),
})

watch(() => currentCustomSpuData.customSpu, () => {
  customSpuRef.value = currentCustomSpuData.customSpu
  const product = createProduct()
  if (product && addToCart.value) {
    addToCart.value.reset()
    addToCart.value.setProduct(product)
  }
  groupCustomSkus()
})

type SimpleSkuItem = SimpleSku & { rowSpan: number }

const customSkusRef = ref<SimpleSkuItem[]>([])

const groupCustomSkus = () => {
  const skuItems = [...props.customSkus].sort((l, r) => {
    return l === r ? 0 : (l > r ? 1 : -1)
  }) as SimpleSkuItem[]
  if (skuItems.length > 0) {
    let currentSpanItem = skuItems[0]
    currentSpanItem.rowSpan = 1
    for (let i = 1; i < skuItems.length; i++) {
      const item = skuItems[i]
      if (item.color?.value === currentSpanItem.color?.value) {
        item.rowSpan = 0
        currentSpanItem.rowSpan = currentSpanItem.rowSpan + 1
      } else {
        item.rowSpan = 1
        currentSpanItem = item
      }
    }
  }
  customSkusRef.value = skuItems
}

// 商品编辑事件
const nameRef = ref('')
const nameEditor = modal()

const handleClickChange = () => {
  nameRef.value = customSpuRef.value?.name as string
}
// 保存编辑
const onSaveName = async () => {
  if (currentCustomSpuData.customSpu) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await updateCustomSpuName(currentCustomSpuData.customSpu.id, { name: nameRef.value })
    customSpuRef.value = Object.assign(customSpuRef.value || {}, { name: nameRef.value }) as CustomSpu
    nameEditor.changeFlag()
  } else
    message.error(t('CustomSpus.CustomSpuDetail.refreshAndRetry'))
}

// 详情
const details = modal()
const detailsValue = ref<SpuImage[]>([])
const detailsShow = async () => {
  currentBtn.value = 'detailsShow'
  details.changeFlag()

  if (currentCustomSpuData.customSpu) {
    const spuId = currentCustomSpuData.customSpu.spu_id
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await fetchSpuImages(spuId as number)
      detailsValue.value = res.spuImg
    } catch (error) {

    }
  } else
    message.error(t('CustomSpus.CustomSpuDetail.refreshAndRetry'))
}

const toDesign = () => {
  if (currentCustomSpuData.customSpu)
    router.push(`/products/custom_spus_design?categoryId=${currentCustomSpuData.customSpu.spu.category_id}&spuId=${currentCustomSpuData.customSpu.spu_id}`)
  else
    message.error(t('CustomSpus.CustomSpuDetail.refreshAndRetry'))
}

// 删除
const router = useRouter()
const deleteChange = () => {
  if (currentCustomSpuData.customSpu) {
    Modal.confirm({
      title: t('CustomSpus.CustomSpuDetail.confirmDeleteTitle'),
      icon: createVNode(ExclamationCircleOutlined),
      centered: true,
      async onOk() {
        const Id = currentCustomSpuData.customSpu!.id
        await deleteCustomSpu({ ids: Id as number })
        router.push('/console/products/custom_spus')
      },
      onCancel() {

      },
    })
  } else
    message.error(t('CustomSpus.CustomSpuDetail.refreshAndRetry'))
}

// 加入购物车
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createProduct = () => {
  const value = (customSpuRef.value || currentCustomSpuData.customSpu)
  if (!value) {
    return undefined
  }

  if (value.customizedImages.length && value.price_detail.custom_price && value.price_detail.custom_price.length) {
    value.customizedImages.forEach((r: any) => {
      value.price_detail.custom_price?.forEach((res: any) => {
        if (r.customableImage.name === res.name)
          res.technologies = r.customableImage.technologies
      })
    })
  }

  return {
    is_custom: true,
    spu_id: value.spu_id,
    name: value.name,
    spu_name: value.spu.name,
    customizedImages: value.customizedImages,
    image: '',
    skus: value.customSkus,
    custom_spu_id: value.id,
    customSkus: value.customSkus,
    price_detail: value.price_detail,
  }
}
const addToCart = ref<any>()
const product = createProduct()
if (product) {
  nextTick(() => {
    addToCart.value.setProduct(product)
  })
}

const addProduct = () => {
  currentBtn.value = 'addProduct'
  addToCart.value.addProduct()
}

const zipAndDownload = async (images: any[], title: string, isObject: boolean): Promise<void> => {
  return new Promise((resolve, reject) => {
    const zip = new JSZip()
    const fileName = title || 'images'
    const imgs = zip.folder(fileName)
    const downImages: any[] = []
    for (let i = 0; i < images.length; i++) {
      const image = new Image()
      // 解决跨域 Canvas 污染问题
      image.setAttribute('crossOrigin', 'anonymous')
      image.onload = function () {
        const canvas = document.createElement('canvas')
        canvas.width = image.width
        canvas.height = image.height
        const context = canvas.getContext('2d')
        context?.drawImage(image, 0, 0, image.width, image.height)
        const url = canvas.toDataURL()
        canvas.toDataURL('image/png')
        const img = { url: url.substring(22), name: i + '.png' }
        if (isObject) {
          img.name = images[i].name
        }
        downImages.push(img)
        if (downImages.length === images.length) {
          if (downImages.length > 0) {
            message.success(t('CustomSpus.CustomSpuDetail.startDownload'))
            resolve()
            for (let k = 0; k < downImages.length; k++) {
              imgs?.file(downImages[k].name, downImages[k].url, { base64: true })
            }
            zip.generateAsync({ type: 'blob' }).then(function (content) {
              saveAs(content, fileName + '.zip')
            })
          } else {
            message.error(t('CustomSpus.CustomSspuDetail.downloadError'))
            reject(new Error('打包出错'))
          }
        }
      }
      if (isObject) {
        image.src = toUrl(images[i].image) || ""
      }
      else {
        image.src = images[i]
      }
    }
  })
}

const downloadImages = async () => {
  try {
    await zipAndDownload(detailsValue.value, t('CustomSpus.CustomSspuDetail.detailPage'), true)
    details.state.flag = false
  } catch (e) {
    details.state.flag = false
  }
}
</script>

<style scoped lang="less">
.detail {
  display: flex;
  flex-direction: column;
  flex: 1;

  .main {
    flex: 1;
    display: flex;
    flex-direction: column;

    .info {
      margin: 10px 0;
      font-size: 20px;
      display: flex;
      align-items: center;

      .name {
        font-family: 'Microsoft YaHei';
        font-weight: 400;
        color: #111111;
        min-width: 100px;
        text-align: center;
      }
    }
  }

  .operation {
    width: 100%;
    display: flex;
    justify-content: end;
    gap: 10px;

    button {
      width: 120px;
      height: 40px;
    }

    .delete {
      color: #DE4A09;
      background: #DE4A0926;

      &:hover {
        border-color: #DE4A09;
      }
    }
  }
}
</style>

<style type="less">
.fb-spu-images-download {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 40px;
}
</style>
